// Sanity – The User Manual for the Human Experience.
// Bilingual content tree (EN + ES).

const translations = {
  en: {
    nav: {
      home: "Home",
      method: "The Method",
      pillars: "Pillars",
      body: "Body",
      mind: "Mind",
      spirit: "Spirit",
      curriculum: "Curriculum",
      about: "About",
      resources: "Resources",
      contact: "Contact",
      cta: "Start Learning",
    },
    waitlist: {
      successEyebrow: "You're enrolled",
      successTitle: "Welcome to Sanity.",
      successBody:
        "You'll receive your first lesson and an update the moment each Volume of the curriculum is released.",
      nameLabel: "Name (optional)",
      emailLabel: "Email",
      nameHint: "One email when each Volume opens. No noise.",
      submit: "Begin the journey",
      sending: "Sending",
      emailRequired: "Please enter your email.",
      genericError: "Something went wrong. Please try again.",
      namePh: "Your name",
      emailPh: "you@example.com",
    },
    footer: {
      tagline:
        "A modern educational system for understanding the body, training the mind, and reconnecting with spirit.",
      explore: "Learn",
      method: "The Method",
      body: "Body",
      mind: "Mind",
      spirit: "Spirit",
      curriculum: "Curriculum",
      about: "About",
      resources: "Resources",
      contact: "Contact",
      stayBlock: "Stay informed",
      staySub: "Receive each Volume of the curriculum as it is released.",
      stayCta: "Begin the journey",
      rights: "Sanity · The User Manual for the Human Experience",
      meta: "Eva Petkoff · Tenerife",
    },
    home: {
      hero: {
        eyebrow: "Sanity",
        title: "The User Manual for the Human Experience.",
        intro:
          "Most people spend their lives using a body they never learned to understand, a mind they were never taught to manage, and a spirit they were never shown how to cultivate.",
        intro2: "Sanity was created to change that.",
        ctaPrimary: "Explore the Curriculum",
        ctaSecondary: "Learn the Method",
        meta: "An educational system · Body · Mind · Spirit",
      },
      marquee: [
        "BODY",
        "MIND",
        "SPIRIT",
        "STRUCTURE",
        "AWARENESS",
        "CONNECTION",
        "EDUCATION",
        "CURRICULUM",
        "PRACTICE",
        "INTEGRATION",
      ],
      problem: {
        eyebrow: "The Problem",
        title: "Nobody teaches us how to be human.",
        intro:
          "Schools teach mathematics, history and language. None teach us the operating system we live inside.",
        cards: [
          {
            tag: "Body",
            title: "Structure",
            body:
              "Movement, breathing, sleep, nutrition, health and physical wellbeing — the structural foundation of every other capacity.",
          },
          {
            tag: "Mind",
            title: "Awareness",
            body:
              "Thoughts, emotions, habits, attention, stress and self-awareness — the inner instrument through which we know.",
          },
          {
            tag: "Spirit",
            title: "Connection",
            body:
              "Presence, meaning, gratitude, purpose and transcendence — the connective tissue between self, others and life.",
          },
        ],
      },
      framework: {
        eyebrow: "The Sanity Framework",
        title: "Three dimensions. One curriculum.",
        intro:
          "Body, Mind and Spirit are not separate disciplines. They are three integrated dimensions of a single human experience — and Sanity is the curriculum that teaches them as one.",
        center: "Sanity",
        labels: {
          body: { title: "Body", sub: "Structure" },
          mind: { title: "Mind", sub: "Awareness" },
          spirit: { title: "Spirit", sub: "Connection" },
        },
        subtopics: {
          body: ["Movement", "Breath", "Sleep", "Nutrition", "Health"],
          mind: ["Attention", "Emotion", "Habits", "Stress", "Self-awareness"],
          spirit: ["Presence", "Meaning", "Purpose", "Gratitude", "Transcendence"],
        },
      },
      curriculum: {
        eyebrow: "The Curriculum",
        title: "A published library.",
        intro:
          "Sanity is being released as a structured curriculum of three Volumes — a comprehensive education in the body, the mind and the spirit.",
        volumes: [
          {
            num: "Volume I",
            title: "User Manual for the Body",
            sub: "Structure",
            body:
              "Movement, breath, sleep, nutrition, posture, regulation. How the body actually works — and how to inhabit it.",
          },
          {
            num: "Volume II",
            title: "User Manual for the Mind",
            sub: "Awareness",
            body:
              "Attention, perception, emotion, habit, stress. How the mind generates experience — and how to study it from the inside.",
          },
          {
            num: "Volume III",
            title: "User Manual for the Spirit",
            sub: "Connection",
            body:
              "Presence, meaning, gratitude, purpose, transcendence. How to cultivate a felt connection to life — without doctrine.",
          },
        ],
        ctaLink: "View the full curriculum",
      },
      method: {
        eyebrow: "The Method",
        title: "From awareness to transformation.",
        intro:
          "Every Volume of Sanity moves the student through the same five-step educational arc. Knowledge alone is not enough — Sanity is designed to be lived.",
        steps: [
          { label: "Awareness", body: "Notice what was previously invisible." },
          { label: "Understanding", body: "Make sense of what you observe." },
          { label: "Application", body: "Put the principles into deliberate practice." },
          { label: "Integration", body: "Weave the practice into daily life." },
          { label: "Transformation", body: "Stable change in how you live." },
        ],
      },
      creator: {
        eyebrow: "The Creator",
        name: "Eva Petkoff",
        roles: [
          "Neurological Physiotherapist",
          "Functional Rehabilitation Specialist",
          "Creator of the Sanity Method",
        ],
        body:
          "Fourteen years of clinical practice with the human body, the nervous system and the inner experience that moves them. Sanity is the structured expression of what she has observed to be missing in both healthcare and schooling: a coherent curriculum for being human.",
        cta: "Read the full story",
      },
      finalCta: {
        title: "Understand yourself. Improve your life experience.",
        sub: "Sanity is a complete curriculum for human development. Begin where you are.",
        button: "Begin the journey",
      },
    },
    method: {
      eyebrow: "The Method",
      title: "How Sanity is taught.",
      intro:
        "The Sanity Method is a five-step educational arc that moves the student from initial observation to lasting change. Every Volume of the curriculum is written around it.",
      steps: [
        {
          label: "Awareness",
          body:
            "The starting point. Before anything can change, it must first be observed. The student learns to notice the previously invisible — the patterns of the body, the mind and the spirit that quietly run a life.",
        },
        {
          label: "Understanding",
          body:
            "Observation alone is not knowledge. Each phenomenon is studied — physiologically, cognitively, philosophically — until the student understands not only what is happening, but why.",
        },
        {
          label: "Application",
          body:
            "Understanding becomes practice. The student is given concrete protocols, exercises and inquiries — small, deliberate actions that translate the curriculum into experience.",
        },
        {
          label: "Integration",
          body:
            "Practice becomes life. Through repetition and structured integration, the new patterns become how the student actually lives — at work, at rest, in relationship.",
        },
        {
          label: "Transformation",
          body:
            "The result is not motivation, novelty or insight. It is stable change. A different relationship with the body, the mind and the spirit, sustained over time.",
        },
      ],
      coda: {
        eyebrow: "The continuous loop",
        title: "An education that returns to itself.",
        body:
          "Transformation is not the end of the curriculum — it is the soil for the next layer of awareness. The five steps are not linear but recursive: each pass reveals a deeper layer to study, understand, apply and integrate.",
      },
    },
    pillars: {
      body: {
        eyebrow: "Pillar · 01",
        word: "Structure",
        title: "Body.",
        intro:
          "The structural foundation. Movement, breath, sleep, nutrition, regulation. Sanity treats the body not as an aesthetic project but as the substrate of every other human capacity.",
        manuscript: {
          eyebrow: "From the Volume",
          title: "An introduction to the Body.",
          paragraphs: [
            "The human body is one of the most remarkable and complex systems in nature. It allows us to move, breathe, think, learn, create, connect, and experience the world. Every aspect of our lives depends upon its ability to adapt, recover and function.",
            "Yet despite its importance, many people receive little education about how the body works or how to care for it across a lifetime. We are often taught to react to health problems after they arise — and given far less attention to the habits and practices that prevent them.",
            "This Volume is a practical understanding of the body and the factors that support long-term health. Rather than focusing solely on the treatment of disease, it emphasises the development of knowledge, awareness, and daily practices that promote physical function, resilience and vitality.",
            "We explore the foundations of human movement, exercise, mobility, posture, breathing, recovery and healthy ageing — and how small, consistent actions accumulate into a meaningful life.",
            "The body is not merely something we possess. It is the foundation through which we experience life — every thought, emotion, relationship and experience is influenced by its condition.",
          ],
        },
        sections: [
          { h: "Movement & posture", b: "How the body is designed to organise itself in space, and how it can be restored when that organisation has been lost." },
          { h: "Breath & nervous system", b: "The body's regulation system — slow, learnable, foundational to every state of mind." },
          { h: "Sleep, nutrition & recovery", b: "The unglamorous infrastructure of a functional human. Studied, not improvised." },
          { h: "Health as practice", b: "Prevention as the new medicine. Health treated as an active discipline, not a passive state." },
        ],
      },
      mind: {
        eyebrow: "Pillar · 02",
        word: "Awareness",
        title: "Mind.",
        intro:
          "The inner instrument through which everything is known. Attention, perception, emotion, habit. Sanity teaches the mind to study itself — not to dominate it, but to use it well.",
        sections: [
          { h: "Attention & perception", b: "How the mind directs itself, what it filters, and how its focus can be trained." },
          { h: "Emotion & regulation", b: "Emotion as data, not noise. Studied as a signal, integrated with the nervous system." },
          { h: "Habits & patterns", b: "The architecture of repetition. How patterns form, how they hold, how they shift." },
          { h: "Stress, focus & cognition", b: "The cognitive cost of modern life — and the structured ways to recover from it." },
        ],
      },
      spirit: {
        eyebrow: "Pillar · 03",
        word: "Connection",
        title: "Spirit.",
        intro:
          "Not religion and not spectacle. Spirit is the felt connection between self, others and life — meaning, presence, gratitude, purpose. Sanity teaches it as a studyable, non-dogmatic field.",
        sections: [
          { h: "Presence & attention", b: "The capacity to be where the body is. The simplest and rarest of human skills." },
          { h: "Meaning & purpose", b: "How a human life is oriented — values clarified, vocation explored, direction chosen." },
          { h: "Gratitude & relationship", b: "The connective tissue. How attention to what is given changes the texture of life." },
          { h: "Transcendence", b: "The experience of being part of something larger. Studied without doctrine." },
        ],
      },
    },
    curriculum: {
      eyebrow: "The Curriculum",
      title: "A library for being human.",
      intro:
        "Sanity is published as a structured curriculum of three Volumes. Each Volume is a complete education in one dimension of the human experience. Together, they form a single, integrated framework.",
      volumes: [
        {
          num: "Volume I",
          title: "User Manual for the Body",
          sub: "Structure",
          body:
            "The structural foundation of being human. Movement, breath, sleep, nutrition, posture, regulation — studied as an integrated curriculum, not a collection of habits.",
          chapters: [
            "Anatomy of presence",
            "Movement & functional design",
            "Breath as a regulation system",
            "Sleep, nutrition & recovery",
            "Posture, somatic awareness",
            "Health as a daily practice",
          ],
          status: "Releasing first",
        },
        {
          num: "Volume II",
          title: "User Manual for the Mind",
          sub: "Awareness",
          body:
            "The inner instrument. How attention works, how emotion functions as data, how patterns form and how they can be studied from the inside.",
          chapters: [
            "Attention & perception",
            "The architecture of emotion",
            "Cognitive patterns & beliefs",
            "Stress & recovery",
            "Habits & self-awareness",
            "The disciplined mind",
          ],
          status: "In preparation",
        },
        {
          num: "Volume III",
          title: "User Manual for the Spirit",
          sub: "Connection",
          body:
            "The connective dimension. Presence, meaning, gratitude, purpose, transcendence — taught as practical, studyable human capacities.",
          chapters: [
            "Presence as a skill",
            "Meaning & values",
            "Gratitude & relationship",
            "Purpose & orientation",
            "Transcendence without doctrine",
            "Integration into daily life",
          ],
          status: "In preparation",
        },
      ],
      cta: {
        title: "Be the first to read each Volume as it is released.",
        sub: "Enrol in the curriculum and receive each Volume the moment it opens.",
      },
    },
    about: {
      eyebrow: "About",
      title: "An educational system,",
      titleEm: "not a personal brand.",
      intro:
        "Sanity is designed to outlive its creator. The method, the curriculum and the framework are built to be transferable — adopted by other educators, institutions and practitioners.",
      whatIs: {
        eyebrow: "What Sanity is",
        title: "A modern educational framework for understanding the human experience.",
        body:
          "Sanity is a structured curriculum of three Volumes — Body, Mind, Spirit — and a method of five steps that moves the student from awareness to transformation. It is published as a library, taught as a system, and designed to scale into schools, clinics and educational programs.",
      },
      meaning: {
        eyebrow: "The Meaning of Sanity",
        title: "Sanity is sanitas — health, soundness, wholeness.",
        lead:
          "Often associated with mental health, sanity is in fact something larger: a state of balance and integration in which the different dimensions of being human work together.",
        paragraphs: [
          "Human beings are multidimensional. We experience life through our bodies, our minds, our emotions, our relationships, and our sense of meaning. These dimensions can be studied separately, but they are deeply interconnected and constantly influence one another.",
          "For this reason, the Sanity curriculum is organised around three foundational dimensions — Body, Mind, Spirit. They are not separate parts of who we are, but different perspectives through which we can better understand ourselves.",
          "Modern education has produced extraordinary advances in knowledge and technology, yet many people receive little guidance in understanding themselves. We learn about the external world; we spend far less time learning about the systems that shape our health, behaviour, relationships and well-being.",
          "The purpose of the Sanity curriculum is to help bridge that gap — by integrating health sciences, psychology, neuroscience, philosophy and human development into a single, practical framework for self-knowledge.",
        ],
        coreEyebrow: "At its core",
        coreLine:
          "The better we understand ourselves, the better equipped we become to care for ourselves, relate to others, and contribute to the world in meaningful ways.",
        triad: [
          { k: "Body", v: "Caring for the body grounds us." },
          { k: "Mind", v: "Training the mind refines us." },
          { k: "Spirit", v: "Cultivating awareness transforms us." },
        ],
      },
      creator: {
        eyebrow: "From the Author",
        title: "Eva Petkoff.",
        roles: [
          "Neurological Physiotherapist",
          "Functional Rehabilitation Specialist",
          "Creator of the Sanity Method",
        ],
        paragraphs: [
          "Sanity was born from a simple observation: many of the challenges people face are not caused by a lack of information, but by a lack of awareness.",
          "Throughout my work as a physiotherapist — and through years of studying human movement, health, behaviour, neuroscience and personal development — I kept returning to the same question. Why do human beings often know what is good for them, yet struggle to live in alignment with that knowledge?",
          "That question led me beyond the body and into the broader exploration of the human experience. My professional background is in physiotherapy and rehabilitation, with a particular interest in human movement, nervous system regulation, breathing, recovery and functional health.",
          "Sanity does not pretend to provide all the answers. It is an invitation to ask better questions — about how we think, how we feel, how we relate to ourselves, to others, and to the world around us.",
          "My hope is that this work encourages curiosity, self-reflection, and a deeper appreciation for the extraordinary complexity of being human.",
        ],
        signature: "— Eva Petkoff",
      },
      stats: [
        { k: "3", v: "Volumes — Body, Mind, Spirit" },
        { k: "5", v: "Steps in the Method" },
        { k: "14", v: "Years of clinical practice" },
        { k: "1", v: "Integrated curriculum" },
      ],
      ctaPrimary: "Explore the Curriculum",
      ctaSecondary: "Read the Method",
    },
    resources: {
      eyebrow: "Resources",
      title: "Reading, references and ongoing notes.",
      intro:
        "A curated, slowly-growing library of the references that inform the Sanity curriculum — and original notes written between the Volumes.",
      categories: [
        {
          tag: "Notes",
          title: "Field notes",
          body:
            "Short essays and observations written from the clinic and the curriculum. Released between the Volumes.",
          link: "Coming soon",
        },
        {
          tag: "Reading",
          title: "Recommended library",
          body:
            "The books, papers and thinkers that shape the Sanity framework — across physiology, neuroscience, philosophy and contemplative practice.",
          link: "Coming soon",
        },
        {
          tag: "Lectures",
          title: "Public lectures",
          body:
            "Talks and conversations with Eva. Selected appearances and educational sessions, organised by Volume.",
          link: "Coming soon",
        },
        {
          tag: "For Institutions",
          title: "Curriculum for institutions",
          body:
            "Information for schools, clinics and educators who wish to integrate Sanity as a structured program.",
          link: "Get in touch",
        },
      ],
      cta: "Subscribe to be notified when each resource is published.",
    },
    contact: {
      eyebrow: "Contact",
      titleA: "Write to",
      titleB: "Eva.",
      intro:
        "For institutional enquiries, lectures, press, mentorship or simply to share your path — every message is read personally.",
      form: {
        nameLabel: "Name *",
        namePh: "Your name",
        emailLabel: "Email *",
        emailPh: "you@example.com",
        subjectLabel: "Subject",
        subjectPh: "Institutions · Press · Lectures · Other",
        messageLabel: "Message *",
        messagePh: "Tell us your context...",
        submit: "Send message",
        sending: "Sending",
        replied: "Replied to within a few days",
        required: "Please complete all required fields.",
        genericError: "Something went wrong. Please try again.",
        successEyebrow: "Message received",
        successTitleA: "Thank you, ",
        successBody:
          "Your message has reached us. We'll respond within a few days.",
      },
      aside: {
        eyebrow: "Direct",
        title: "Or reach out directly.",
        emailLabel: "Email",
        basedLabel: "Based in",
        basedValue: "Tenerife · Canary Islands",
        followLabel: "Follow",
        quote:
          "“Sanity is built to be transferable. The work is bigger than the person who began it.”",
      },
    },
  },

  // ============================ ESPAÑOL ============================
  es: {
    nav: {
      home: "Inicio",
      method: "El Método",
      pillars: "Pilares",
      body: "Cuerpo",
      mind: "Mente",
      spirit: "Espíritu",
      curriculum: "Currículo",
      about: "Nosotros",
      resources: "Recursos",
      contact: "Contacto",
      cta: "Empezar a aprender",
    },
    waitlist: {
      successEyebrow: "Matrícula recibida",
      successTitle: "Bienvenida a Sanity.",
      successBody:
        "Recibirás tu primera lección y un aviso en el momento en que cada Volumen del currículo sea publicado.",
      nameLabel: "Nombre (opcional)",
      emailLabel: "Correo",
      nameHint: "Un correo cuando cada Volumen abra. Sin ruido.",
      submit: "Comenzar el camino",
      sending: "Enviando",
      emailRequired: "Por favor, introduce tu correo.",
      genericError: "Algo salió mal. Inténtalo de nuevo.",
      namePh: "Tu nombre",
      emailPh: "tu@correo.com",
    },
    footer: {
      tagline:
        "Un sistema educativo moderno para entender el cuerpo, entrenar la mente y reconectar con el espíritu.",
      explore: "Aprender",
      method: "El Método",
      body: "Cuerpo",
      mind: "Mente",
      spirit: "Espíritu",
      curriculum: "Currículo",
      about: "Nosotros",
      resources: "Recursos",
      contact: "Contacto",
      stayBlock: "Mantente al día",
      staySub: "Recibe cada Volumen del currículo a medida que se publica.",
      stayCta: "Comenzar el camino",
      rights: "Sanity · El Manual del Usuario para la Experiencia Humana",
      meta: "Eva Petkoff · Tenerife",
    },
    home: {
      hero: {
        eyebrow: "Sanity",
        title: "El Manual del Usuario para la Experiencia Humana.",
        intro:
          "La mayoría pasa la vida usando un cuerpo que nunca aprendió a entender, una mente que nunca le enseñaron a gestionar, y un espíritu que nadie le mostró cómo cultivar.",
        intro2: "Sanity nace para cambiar eso.",
        ctaPrimary: "Explorar el Currículo",
        ctaSecondary: "Conocer el Método",
        meta: "Un sistema educativo · Cuerpo · Mente · Espíritu",
      },
      marquee: [
        "CUERPO",
        "MENTE",
        "ESPÍRITU",
        "ESTRUCTURA",
        "CONSCIENCIA",
        "CONEXIÓN",
        "EDUCACIÓN",
        "CURRÍCULO",
        "PRÁCTICA",
        "INTEGRACIÓN",
      ],
      problem: {
        eyebrow: "El Problema",
        title: "Nadie nos enseña a ser humanos.",
        intro:
          "La escuela enseña matemáticas, historia e idiomas. Ninguna enseña el sistema operativo en el que vivimos.",
        cards: [
          {
            tag: "Cuerpo",
            title: "Estructura",
            body:
              "Movimiento, respiración, sueño, nutrición, salud y bienestar físico — la base estructural de todas las demás capacidades.",
          },
          {
            tag: "Mente",
            title: "Consciencia",
            body:
              "Pensamientos, emociones, hábitos, atención, estrés y auto-consciencia — el instrumento interno con el que conocemos.",
          },
          {
            tag: "Espíritu",
            title: "Conexión",
            body:
              "Presencia, sentido, gratitud, propósito y trascendencia — el tejido conectivo entre uno, los demás y la vida.",
          },
        ],
      },
      framework: {
        eyebrow: "El Marco Sanity",
        title: "Tres dimensiones. Un currículo.",
        intro:
          "Cuerpo, Mente y Espíritu no son disciplinas separadas. Son tres dimensiones integradas de una sola experiencia humana — y Sanity es el currículo que las enseña como una.",
        center: "Sanity",
        labels: {
          body: { title: "Cuerpo", sub: "Estructura" },
          mind: { title: "Mente", sub: "Consciencia" },
          spirit: { title: "Espíritu", sub: "Conexión" },
        },
        subtopics: {
          body: ["Movimiento", "Respiración", "Sueño", "Nutrición", "Salud"],
          mind: ["Atención", "Emoción", "Hábitos", "Estrés", "Auto-consciencia"],
          spirit: ["Presencia", "Sentido", "Propósito", "Gratitud", "Trascendencia"],
        },
      },
      curriculum: {
        eyebrow: "El Currículo",
        title: "Una biblioteca publicada.",
        intro:
          "Sanity se publica como un currículo estructurado de tres Volúmenes — una educación completa del cuerpo, la mente y el espíritu.",
        volumes: [
          {
            num: "Volumen I",
            title: "Manual del Usuario para el Cuerpo",
            sub: "Estructura",
            body:
              "Movimiento, respiración, sueño, nutrición, postura, regulación. Cómo funciona realmente el cuerpo — y cómo habitarlo.",
          },
          {
            num: "Volumen II",
            title: "Manual del Usuario para la Mente",
            sub: "Consciencia",
            body:
              "Atención, percepción, emoción, hábito, estrés. Cómo la mente genera experiencia — y cómo estudiarla desde dentro.",
          },
          {
            num: "Volumen III",
            title: "Manual del Usuario para el Espíritu",
            sub: "Conexión",
            body:
              "Presencia, sentido, gratitud, propósito, trascendencia. Cómo cultivar una conexión sentida con la vida — sin doctrina.",
          },
        ],
        ctaLink: "Ver el currículo completo",
      },
      method: {
        eyebrow: "El Método",
        title: "De la consciencia a la transformación.",
        intro:
          "Cada Volumen de Sanity lleva al estudiante a través del mismo arco educativo de cinco pasos. El conocimiento no basta — Sanity está diseñado para ser vivido.",
        steps: [
          { label: "Consciencia", body: "Notar lo que antes era invisible." },
          { label: "Comprensión", body: "Dar sentido a lo observado." },
          { label: "Aplicación", body: "Llevar los principios a la práctica deliberada." },
          { label: "Integración", body: "Tejer la práctica en la vida diaria." },
          { label: "Transformación", body: "Cambio estable en cómo vives." },
        ],
      },
      creator: {
        eyebrow: "La Creadora",
        name: "Eva Petkoff",
        roles: [
          "Fisioterapeuta Neurológica",
          "Especialista en Rehabilitación Funcional",
          "Creadora del Método Sanity",
        ],
        body:
          "Catorce años de práctica clínica con el cuerpo humano, el sistema nervioso y la experiencia interna que los mueve. Sanity es la expresión estructurada de lo que ha observado que falta tanto en la sanidad como en la educación: un currículo coherente para habitar lo humano.",
        cta: "Leer la historia completa",
      },
      finalCta: {
        title: "Entiéndete. Mejora tu experiencia de vida.",
        sub: "Sanity es un currículo completo para el desarrollo humano. Empieza donde estás.",
        button: "Comenzar el camino",
      },
    },
    method: {
      eyebrow: "El Método",
      title: "Cómo se enseña Sanity.",
      intro:
        "El Método Sanity es un arco educativo de cinco pasos que lleva al estudiante desde la primera observación hasta el cambio sostenido. Cada Volumen del currículo está escrito en torno a él.",
      steps: [
        {
          label: "Consciencia",
          body:
            "El punto de partida. Antes de cambiar algo, hay que verlo. El estudiante aprende a notar lo previamente invisible — los patrones del cuerpo, la mente y el espíritu que dirigen una vida en silencio.",
        },
        {
          label: "Comprensión",
          body:
            "Observar no es conocer. Cada fenómeno se estudia — fisiológica, cognitiva y filosóficamente — hasta que el estudiante entiende no solo qué ocurre, sino por qué.",
        },
        {
          label: "Aplicación",
          body:
            "La comprensión se convierte en práctica. El estudiante recibe protocolos concretos, ejercicios e indagaciones — acciones pequeñas y deliberadas que traducen el currículo en experiencia.",
        },
        {
          label: "Integración",
          body:
            "La práctica se convierte en vida. Mediante repetición e integración estructurada, los nuevos patrones se convierten en la forma real de vivir — en el trabajo, en el descanso, en la relación.",
        },
        {
          label: "Transformación",
          body:
            "El resultado no es motivación, novedad ni insight. Es cambio estable. Una relación diferente con el cuerpo, la mente y el espíritu, sostenida en el tiempo.",
        },
      ],
      coda: {
        eyebrow: "El bucle continuo",
        title: "Una educación que vuelve sobre sí misma.",
        body:
          "La transformación no es el final del currículo — es el suelo para la siguiente capa de consciencia. Los cinco pasos no son lineales sino recursivos: cada vuelta revela una capa más profunda que estudiar, comprender, aplicar e integrar.",
      },
    },
    pillars: {
      body: {
        eyebrow: "Pilar · 01",
        word: "Estructura",
        title: "Cuerpo.",
        intro:
          "La base estructural. Movimiento, respiración, sueño, nutrición, regulación. Sanity trata el cuerpo no como proyecto estético, sino como sustrato de toda otra capacidad humana.",
        manuscript: {
          eyebrow: "Del Volumen",
          title: "Una introducción al Cuerpo.",
          paragraphs: [
            "El cuerpo humano es uno de los sistemas más notables y complejos de la naturaleza. Nos permite movernos, respirar, pensar, aprender, crear, relacionarnos y experimentar el mundo. Cada aspecto de nuestra vida depende de su capacidad para adaptarse, recuperarse y funcionar.",
            "A pesar de su importancia, muchas personas reciben poca educación sobre cómo funciona el cuerpo o cómo cuidarlo a lo largo de la vida. A menudo se nos enseña a reaccionar ante los problemas de salud después de que aparecen — y se presta mucha menos atención a los hábitos y prácticas que los previenen.",
            "Este Volumen ofrece una comprensión práctica del cuerpo y de los factores que sostienen la salud a largo plazo. Más que centrarse en el tratamiento de la enfermedad, enfatiza el desarrollo del conocimiento, la consciencia y las prácticas diarias que promueven la función, la resiliencia y la vitalidad.",
            "Exploramos los fundamentos del movimiento humano, el ejercicio, la movilidad, la postura, la respiración, la recuperación y el envejecimiento saludable — y cómo pequeñas acciones constantes se acumulan en una vida con sentido.",
            "El cuerpo no es solo algo que poseemos. Es la base a través de la cual experimentamos la vida — cada pensamiento, emoción, relación y experiencia está influido por su condición.",
          ],
        },
        sections: [
          { h: "Movimiento y postura", b: "Cómo el cuerpo está diseñado para organizarse en el espacio, y cómo puede restaurarse cuando esa organización se ha perdido." },
          { h: "Respiración y sistema nervioso", b: "El sistema de regulación del cuerpo — lento, aprendible, fundamental para todo estado mental." },
          { h: "Sueño, nutrición y recuperación", b: "La infraestructura poco glamurosa de un ser humano funcional. Estudiada, no improvisada." },
          { h: "Salud como práctica", b: "Prevención como nueva medicina. La salud tratada como disciplina activa, no como estado pasivo." },
        ],
      },
      mind: {
        eyebrow: "Pilar · 02",
        word: "Consciencia",
        title: "Mente.",
        intro:
          "El instrumento interno con el que todo se conoce. Atención, percepción, emoción, hábito. Sanity enseña a la mente a estudiarse a sí misma — no para dominarla, sino para usarla bien.",
        sections: [
          { h: "Atención y percepción", b: "Cómo se dirige la mente, qué filtra y cómo puede entrenarse su foco." },
          { h: "Emoción y regulación", b: "La emoción como dato, no como ruido. Estudiada como señal, integrada con el sistema nervioso." },
          { h: "Hábitos y patrones", b: "La arquitectura de la repetición. Cómo se forman los patrones, cómo se sostienen y cómo cambian." },
          { h: "Estrés, foco y cognición", b: "El coste cognitivo de la vida moderna — y las formas estructuradas de recuperarse." },
        ],
      },
      spirit: {
        eyebrow: "Pilar · 03",
        word: "Conexión",
        title: "Espíritu.",
        intro:
          "Ni religión ni espectáculo. El espíritu es la conexión sentida entre uno, los demás y la vida — sentido, presencia, gratitud, propósito. Sanity lo enseña como un campo estudiable y no doctrinario.",
        sections: [
          { h: "Presencia y atención", b: "La capacidad de estar donde el cuerpo está. La habilidad humana más simple y más rara." },
          { h: "Sentido y propósito", b: "Cómo se orienta una vida humana — valores clarificados, vocación explorada, dirección elegida." },
          { h: "Gratitud y relación", b: "El tejido conectivo. Cómo la atención a lo dado cambia la textura de la vida." },
          { h: "Trascendencia", b: "La experiencia de ser parte de algo mayor. Estudiada sin doctrina." },
        ],
      },
    },
    curriculum: {
      eyebrow: "El Currículo",
      title: "Una biblioteca para habitar lo humano.",
      intro:
        "Sanity se publica como un currículo estructurado de tres Volúmenes. Cada Volumen es una educación completa de una dimensión de la experiencia humana. Juntos, forman un marco único e integrado.",
      volumes: [
        {
          num: "Volumen I",
          title: "Manual del Usuario para el Cuerpo",
          sub: "Estructura",
          body:
            "La base estructural de ser humano. Movimiento, respiración, sueño, nutrición, postura, regulación — estudiados como currículo integrado, no como colección de hábitos.",
          chapters: [
            "Anatomía de la presencia",
            "Movimiento y diseño funcional",
            "La respiración como regulación",
            "Sueño, nutrición y recuperación",
            "Postura y consciencia somática",
            "La salud como práctica diaria",
          ],
          status: "Primer lanzamiento",
        },
        {
          num: "Volumen II",
          title: "Manual del Usuario para la Mente",
          sub: "Consciencia",
          body:
            "El instrumento interno. Cómo funciona la atención, cómo opera la emoción como dato, cómo se forman los patrones y cómo pueden estudiarse desde dentro.",
          chapters: [
            "Atención y percepción",
            "La arquitectura de la emoción",
            "Patrones cognitivos y creencias",
            "Estrés y recuperación",
            "Hábitos y auto-consciencia",
            "La mente disciplinada",
          ],
          status: "En preparación",
        },
        {
          num: "Volumen III",
          title: "Manual del Usuario para el Espíritu",
          sub: "Conexión",
          body:
            "La dimensión conectiva. Presencia, sentido, gratitud, propósito, trascendencia — enseñadas como capacidades humanas prácticas y estudiables.",
          chapters: [
            "La presencia como habilidad",
            "Sentido y valores",
            "Gratitud y relación",
            "Propósito y orientación",
            "Trascendencia sin doctrina",
            "Integración en la vida diaria",
          ],
          status: "En preparación",
        },
      ],
      cta: {
        title: "Sé de las primeras en leer cada Volumen cuando se publique.",
        sub: "Inscríbete en el currículo y recibe cada Volumen en el momento en que abra.",
      },
    },
    about: {
      eyebrow: "Nosotros",
      title: "Un sistema educativo,",
      titleEm: "no una marca personal.",
      intro:
        "Sanity está diseñado para sobrevivir a su creadora. El método, el currículo y el marco están construidos para ser transferibles — adoptados por otros educadores, instituciones y profesionales.",
      whatIs: {
        eyebrow: "Qué es Sanity",
        title: "Un marco educativo moderno para entender la experiencia humana.",
        body:
          "Sanity es un currículo estructurado de tres Volúmenes — Cuerpo, Mente, Espíritu — y un método de cinco pasos que lleva al estudiante desde la consciencia hasta la transformación. Se publica como una biblioteca, se enseña como un sistema, y está diseñado para escalar a escuelas, clínicas y programas educativos.",
      },
      meaning: {
        eyebrow: "El Significado de Sanity",
        title: "Sanity viene de sanitas — salud, solidez, integridad.",
        lead:
          "Aunque suele asociarse a la salud mental, sanity es algo más amplio: un estado de equilibrio e integración en el que las distintas dimensiones del ser humano trabajan en conjunto.",
        paragraphs: [
          "Los seres humanos somos multidimensionales. Experimentamos la vida a través del cuerpo, la mente, las emociones, las relaciones y nuestro sentido de propósito. Estas dimensiones pueden estudiarse por separado, pero están profundamente interconectadas y se influyen mutuamente.",
          "Por eso el currículo Sanity se organiza en torno a tres dimensiones fundamentales — Cuerpo, Mente, Espíritu. No son partes separadas de lo que somos, sino perspectivas distintas desde las que podemos comprendernos mejor.",
          "La educación moderna ha producido avances extraordinarios en conocimiento y tecnología, pero muchas personas reciben poca orientación para entenderse a sí mismas. Aprendemos sobre el mundo exterior; dedicamos mucho menos tiempo a los sistemas que dan forma a nuestra salud, conducta, relaciones y bienestar.",
          "El propósito del currículo Sanity es ayudar a cerrar esa brecha — integrando ciencias de la salud, psicología, neurociencia, filosofía y desarrollo humano en un marco práctico de autoconocimiento.",
        ],
        coreEyebrow: "En esencia",
        coreLine:
          "Cuanto mejor nos comprendemos, mejor equipados estamos para cuidarnos, relacionarnos con los demás y contribuir al mundo de forma significativa.",
        triad: [
          { k: "Cuerpo", v: "Cuidar el cuerpo nos enraíza." },
          { k: "Mente", v: "Entrenar la mente nos refina." },
          { k: "Espíritu", v: "Cultivar la consciencia nos transforma." },
        ],
      },
      creator: {
        eyebrow: "De la autora",
        title: "Eva Petkoff.",
        roles: [
          "Fisioterapeuta Neurológica",
          "Especialista en Rehabilitación Funcional",
          "Creadora del Método Sanity",
        ],
        paragraphs: [
          "Sanity nació de una observación simple: muchos de los retos que enfrentan las personas no surgen de la falta de información, sino de la falta de consciencia.",
          "A lo largo de mi trabajo como fisioterapeuta — y de años estudiando el movimiento humano, la salud, la conducta, la neurociencia y el desarrollo personal — volví una y otra vez a la misma pregunta. ¿Por qué los seres humanos sabemos lo que nos hace bien, y aun así nos cuesta vivir en coherencia con ese conocimiento?",
          "Esa pregunta me llevó más allá del cuerpo, hacia la exploración más amplia de la experiencia humana. Mi formación es la fisioterapia y la rehabilitación, con un interés particular en el movimiento, la regulación del sistema nervioso, la respiración, la recuperación y la salud funcional.",
          "Sanity no pretende ofrecer todas las respuestas. Es una invitación a hacer mejores preguntas — sobre cómo pensamos, cómo sentimos, cómo nos relacionamos con nosotros mismos, con los demás y con el mundo.",
          "Mi deseo es que este trabajo despierte curiosidad, autoreflexión, y una apreciación más profunda por la extraordinaria complejidad de ser humano.",
        ],
        signature: "— Eva Petkoff",
      },
      stats: [
        { k: "3", v: "Volúmenes — Cuerpo, Mente, Espíritu" },
        { k: "5", v: "Pasos del Método" },
        { k: "14", v: "Años de práctica clínica" },
        { k: "1", v: "Currículo integrado" },
      ],
      ctaPrimary: "Explorar el Currículo",
      ctaSecondary: "Leer el Método",
    },
    resources: {
      eyebrow: "Recursos",
      title: "Lecturas, referencias y notas continuas.",
      intro:
        "Una biblioteca cuidada y de crecimiento lento, con las referencias que informan el currículo Sanity — y notas originales escritas entre los Volúmenes.",
      categories: [
        {
          tag: "Notas",
          title: "Notas de campo",
          body:
            "Ensayos breves y observaciones escritas desde la clínica y el currículo. Publicadas entre los Volúmenes.",
          link: "Próximamente",
        },
        {
          tag: "Lecturas",
          title: "Biblioteca recomendada",
          body:
            "Los libros, artículos y pensadores que dan forma al marco Sanity — fisiología, neurociencia, filosofía y práctica contemplativa.",
          link: "Próximamente",
        },
        {
          tag: "Conferencias",
          title: "Conferencias públicas",
          body:
            "Charlas y conversaciones con Eva. Apariciones seleccionadas y sesiones educativas, organizadas por Volumen.",
          link: "Próximamente",
        },
        {
          tag: "Para Instituciones",
          title: "Currículo para instituciones",
          body:
            "Información para escuelas, clínicas y educadores que deseen integrar Sanity como programa estructurado.",
          link: "Contactar",
        },
      ],
      cta: "Suscríbete para recibir aviso cuando cada recurso se publique.",
    },
    contact: {
      eyebrow: "Contacto",
      titleA: "Escribe a",
      titleB: "Eva.",
      intro:
        "Para consultas institucionales, conferencias, prensa, mentoría o simplemente para compartir tu camino — cada mensaje se lee personalmente.",
      form: {
        nameLabel: "Nombre *",
        namePh: "Tu nombre",
        emailLabel: "Correo *",
        emailPh: "tu@correo.com",
        subjectLabel: "Asunto",
        subjectPh: "Instituciones · Prensa · Conferencias · Otro",
        messageLabel: "Mensaje *",
        messagePh: "Cuéntanos tu contexto...",
        submit: "Enviar mensaje",
        sending: "Enviando",
        replied: "Respondemos en pocos días",
        required: "Por favor completa los campos requeridos.",
        genericError: "Algo salió mal. Inténtalo de nuevo.",
        successEyebrow: "Mensaje recibido",
        successTitleA: "Gracias, ",
        successBody:
          "Tu mensaje ha llegado. Te responderemos en pocos días.",
      },
      aside: {
        eyebrow: "Directo",
        title: "O contáctanos directamente.",
        emailLabel: "Correo",
        basedLabel: "Basado en",
        basedValue: "Tenerife · Islas Canarias",
        followLabel: "Seguir",
        quote:
          "“Sanity está construido para ser transferible. El trabajo es más grande que la persona que lo empezó.”",
      },
    },
  },
};

export default translations;
